import axios from 'axios';

// In the built (hosted) app, the backend serves this app from the same origin,
// so use a relative URL. In local dev the React server runs on :3000, so talk
// to the backend on :3001 directly.
const baseURL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
// Origin the backend is served from (empty = same origin in prod). Used to resolve /api/… asset
// URLs (e.g. images embedded in Markdown) so a raw <img src> hits the backend, not the page origin.
export const apiOrigin = baseURL.replace(/\/api$/, '');
const api = axios.create({ baseURL });

const emit = (name, detail) => {
  try { window.dispatchEvent(detail ? new CustomEvent(name, { detail }) : new Event(name)); }
  catch { /* noop */ }
};

// While a new version is deploying (Render restarts the backend), requests fail with a network
// error or a gateway status for ~30–90s. Rather than surfacing an error and clearing the screen,
// we quietly retry until the backend is back — so the current view stays put and the request just
// completes once the update finishes. A "reconnecting" ribbon (App.js) shows meanwhile.
const RETRY_WINDOW_MS = 150 * 1000; // keep retrying for up to ~2.5 min (a deploy is usually quicker)
const isDeployOutage = (err) => {
  const cfg = err.config || {};
  if (cfg.__noRetry) return false;
  if (err.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(err))) return false;
  const method = (cfg.method || 'get').toLowerCase();
  const status = err.response && err.response.status;
  // No response at all = connection refused/reset (backend down) — safe to retry any method, the
  // request never reached the server. A 502/503 from the proxy means the same. A 504 (timeout)
  // might have executed, so only retry it for read-only requests.
  if (!err.response) return true;
  // A 502 or 503 means one of two very different things, and telling them apart matters.
  //
  // From the proxy while Render restarts, it is a deploy: no body, and retrying is exactly right.
  // From this app it means a third party refused us — Xero declining to void a bill whose stock
  // has been sold, say — and that carries a JSON error saying so. Retrying that is wrong twice
  // over: it re-sends a write that already ran, and it tells the user the Hub is upgrading when
  // what actually happened is that Xero said no. The refusal has to reach the screen.
  if (status === 502 || status === 503) {
    const body = err.response.data;
    // Except a rate limit, which is a third thing again: not a deploy, not a refusal, just
    // "later". It carries our JSON error like any refusal, so the test above would stop
    // retrying it — and a per-minute limit is the one case where waiting and retrying almost
    // always works. Xero even says how long to wait.
    if (body && typeof body === 'object' && body.rate_limited) return true;
    const oursWithAReason = body && typeof body === 'object' && typeof body.error === 'string';
    return !oursWithAReason;
  }
  if (status === 504 && (method === 'get' || method === 'head')) return true;
  return false;
};

// Wire a client: retry through a deploy outage, and (optionally) handle 401s.
function withResilience(client, on401) {
  client.interceptors.response.use(
    (r) => { emit('pm-backend-online'); return r; },
    async (err) => {
      const status = err.response && err.response.status;
      if (status === 401 && typeof on401 === 'function') { on401(err); return Promise.reject(err); }
      if (isDeployOutage(err)) {
        const cfg = err.config;
        if (!cfg.__deadline) cfg.__deadline = Date.now() + RETRY_WINDOW_MS;
        if (Date.now() < cfg.__deadline) {
          // Xero publishes how long to wait on a rate limit, and its own guidance is to use it
          // rather than invent a backoff. Capped so a long Retry-After cannot hold the request
          // past the deadline that ends the whole thing.
          const askedFor = Number(err.response && err.response.data && err.response.data.retry_after) || 0;
          const limited = !!(err.response && err.response.data && err.response.data.rate_limited);
          // Waiting out a rate limit is not a deploy, and saying "The Hub is upgrading" while it
          // happens is a false statement in the one run of work that was about not making them.
          // Retrying it was the right call; the ribbon just had one thing to say.
          emit(limited ? 'pm-backend-busy' : 'pm-backend-offline', { seconds: askedFor });
          cfg.__tries = (cfg.__tries || 0) + 1;
          const delay = askedFor > 0
            ? Math.min(askedFor * 1000, 60000)
            : Math.min(3000, 600 * cfg.__tries); // ramp up to 3s between tries
          await new Promise((res) => setTimeout(res, delay));
          return client(cfg); // re-issue the same request
        }
      }
      return Promise.reject(err);
    }
  );
}

// Attach the saved auth token (if any) to every request.
// What day it is where this person is.
//
// The server has only ever known Sydney, and stamps the calendar day there onto everything it
// dates — which is right for the practice and wrong for the person. Somebody working an evening
// outside Sydney has their work filed on a day they have not reached yet, and anybody working
// past midnight where they are has one session split across two dates.
//
// The browser is the only thing that knows the answer, so it says so on every request rather
// than on the two that happen to need it today. Nothing is stored about the person and nothing
// has to be set up; a zone the server does not recognise is ignored and Sydney stands.
const localZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
};

api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('pm_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  const tz = localZone();
  if (tz) cfg.headers['X-Tz'] = tz;
  return cfg;
});

// On 401 (expired/invalid token), drop it and signal the app to show the login.
// The public client portal (/client/...) is exempt so clients never see a login.
withResilience(api, () => {
  if (!window.location.pathname.startsWith('/client/')) {
    localStorage.removeItem('pm_token');
    window.dispatchEvent(new Event('pm-unauthorized'));
  }
});

// Separate client portal API — uses the portal session token (clients/contacts), not the staff token.
const portalApi = axios.create({ baseURL });
portalApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('pm_portal_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
withResilience(portalApi, null); // portal rides out deploys too, but never force a staff login
// The bearer header on its own, for the few things that cannot go through axios — a file
// download has to be fetched and turned into a blob, and a plain link cannot carry a token.
export const authHeader = () => {
  const t = localStorage.getItem('pm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// The login token itself, for the one element that cannot be given a header: a <video> tag,
// which fetches its own bytes and can only be told who is asking through the URL.
export const getStaffToken = () => localStorage.getItem('pm_token');

export const PORTAL_BASE = baseURL;
export const getPortalToken = () => localStorage.getItem('pm_portal_token');
export { portalApi };

// Base URL for client-facing links (magic links etc.). Defaults to the current origin, and
// is updated from /api/me's portal_url so links point at the portal app when one is set up.
let portalLinkBase = window.location.origin;
export const setPortalLinkBase = (u) => { if (u) portalLinkBase = String(u).replace(/\/$/, ''); };
export const getPortalLinkBase = () => portalLinkBase;
// Is this browser tab running on the portal subdomain? (portal./my./client./books.)
export const isPortalHost = /^(portal|my|client|books)\./i.test(window.location.hostname);

// One request for the same thing at the same moment. The Work page and the start-of-day prompt
// both load /work as the app opens; this hands the second caller the first one's request while it
// is still in flight — and, only where the caller says a few seconds old is fine (`maxAgeMs`), the
// answer that has just come back. The default is 0: a screen reloading after a change always gets
// fresh data.
const inFlight = new Map();
const recent = new Map(); // path → { at, res }
export function getShared(path, { maxAgeMs = 0 } = {}) {
  const r = recent.get(path);
  if (maxAgeMs > 0 && r && Date.now() - r.at <= maxAgeMs) return Promise.resolve(r.res);
  if (inFlight.has(path)) return inFlight.get(path);
  const p = api.get(path)
    .then(res => { recent.set(path, { at: Date.now(), res }); return res; })
    .finally(() => inFlight.delete(path));
  inFlight.set(path, p);
  return p;
}

export default api;
